import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

interface Vehicle {
  id: string;
  registration_number: string;
  make?: string;
  model?: string;
  year?: number;
  vehicle_class: string;
  status: string;
  created_at: string;
}

const Vehicles: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    registration_number: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    vehicle_class: 'class_2'
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/vehicles`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      setFormData({ registration_number: '', make: '', model: '', year: new Date().getFullYear(), vehicle_class: 'class_2' });
      fetchVehicles();
    } catch (error) {
      console.error('Error creating vehicle:', error);
      alert('Failed to create vehicle');
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_BASE}/vehicles/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchVehicles();
    } catch (error) {
      console.error('Error updating vehicle status:', error);
      alert('Failed to update vehicle status');
    }
  };

  const deleteVehicle = async (id: string, registration: string) => {
    const dontPromptAgain = localStorage.getItem('dontPromptDeleteVehicle') === 'true';
    
    if (!dontPromptAgain && !window.confirm(`Are you sure you want to delete vehicle ${registration}? All inspections and defects will be preserved.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/vehicles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Vehicle deleted successfully');
      fetchVehicles();
    } catch (error: any) {
      console.error('Error deleting vehicle:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to delete vehicle';
      alert(`Failed to delete vehicle: ${errorMsg}`);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Vehicles</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add Vehicle
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {vehicles.map((vehicle) => (
          <div key={vehicle.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-bold">{vehicle.registration_number}</h3>
              <span className={`px-2 py-1 text-xs rounded ${
                vehicle.status === 'active' ? 'bg-green-100 text-green-800' :
                vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {vehicle.status}
              </span>
            </div>
            <p className="text-gray-600 mb-1">
              {vehicle.make} {vehicle.model} {vehicle.year}
            </p>
            <p className="text-sm text-blue-600 font-semibold mb-4">
              {vehicle.vehicle_class === 'class_1' ? 'Class 1 (Articulated)' : 'Class 2 (Rigid)'}
            </p>
            <div className="space-y-2">
              <div className="flex gap-2">
                <button
                  onClick={() => updateStatus(vehicle.id, 'active')}
                  className="flex-1 px-3 py-1 text-sm bg-green-100 text-green-800 rounded hover:bg-green-200"
                >
                  Active
                </button>
                <button
                  onClick={() => updateStatus(vehicle.id, 'maintenance')}
                  className="flex-1 px-3 py-1 text-sm bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                >
                  Maintenance
                </button>
                <button
                  onClick={() => updateStatus(vehicle.id, 'inactive')}
                  className="flex-1 px-3 py-1 text-sm bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
                >
                  Inactive
                </button>
              </div>
              <button
                onClick={() => deleteVehicle(vehicle.id, vehicle.registration_number)}
                className="w-full px-3 py-1 text-sm bg-red-100 text-red-800 rounded hover:bg-red-200 font-medium"
              >
                Delete Vehicle
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">Add New Vehicle</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Registration Number</label>
                <input
                  type="text"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="AB12 CDE"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Make</label>
                <input
                  type="text"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Volvo"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Model</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="FH16"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Year</label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Vehicle Class</label>
                <select
                  value={formData.vehicle_class}
                  onChange={(e) => setFormData({ ...formData, vehicle_class: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="class_2">Class 2 (Rigid Lorry)</option>
                  <option value="class_1">Class 1 (Articulated Lorry)</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add Vehicle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
